import React, { useState } from 'react';
import { Mail, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { requestMagicLink } from '../../lib/auth.service';
import toast from 'react-hot-toast';

interface MagicLinkFormProps {
  onSuccess?: () => void;
}

const MagicLinkForm: React.FC<MagicLinkFormProps> = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!email) {
        throw new Error('Please enter your email address');
      }

      await requestMagicLink(email);
      
      setSent(true);
      toast.success('Password reset link sent! Please check your email.');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Magic link error:', error);
      setError(error instanceof Error ? error.message : 'Failed to send password reset link');
      toast.error('Failed to send password reset link');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center">
        <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Check your email</h2>
        <p className="text-gray-600 mb-4">
          We've sent a password reset link to <strong>{email}</strong>
        </p>
        <p className="text-gray-500 text-sm">
          Click the link in the email to sign in to your account.
        </p>
        <button
          onClick={() => setSent(false)}
          className="mt-6 text-primary hover:text-primary-dark font-medium"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email address
        </label>
        <div className="mt-1 relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            placeholder="you@example.com"
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full justify-center"
        disabled={loading}
      >
        {loading ? (
          <div className="flex items-center">
            <span className="loader mr-3" />
            Sending...
          </div>
        ) : (
          'Send Password Reset Link'
        )}
      </Button>
    </form>
  );
};

export default MagicLinkForm;